// src/edu-world/adapters/minigame-adapter.ts
// Counting minigame stub for Phase 1 vertical slice.
// Simple Phaser scene: shows flowers, child taps to count, returns success.
// TODO: Phase 2 — adapt existing engine/mechanics/count_objects.js properly.

import Phaser from 'phaser';
import type { MinigameInput, MinigameOutput } from '../types';
import type { EventBus } from '../core/event-bus';

interface CountingMinigameData {
  input: MinigameInput;
  eventBus: EventBus;
  onComplete: (output: MinigameOutput) => void;
}

export class CountingMinigame extends Phaser.Scene {
  private input!: MinigameInput;
  private onComplete!: (output: MinigameOutput) => void;
  private startTime: number = 0;
  private attempts: number = 0;
  private targetCount: number = 4;
  private currentCount: number = 0;
  private flowers: Phaser.GameObjects.Arc[] = [];
  private countText!: Phaser.GameObjects.Text;
  private promptText!: Phaser.GameObjects.Text;

  // ── Duration limit (Requirement 12.5) ──
  private static readonly MAX_DURATION_MS = 180_000; // 3 minutes
  private timerEvent?: Phaser.Time.TimerEvent;
  private timerDot?: Phaser.GameObjects.Arc;
  private timerDotTween?: Phaser.Tweens.Tween;
  private isCompleted: boolean = false;

  constructor() {
    super({ key: 'CountingMinigame' });
  }

  init(data: CountingMinigameData): void {
    this.input = data.input;
    this.onComplete = data.onComplete;
    this.startTime = Date.now();
    this.attempts = 0;
    this.currentCount = 0;
    this.isCompleted = false;

    // Extract target count from config data
    const configData = this.input.config.data as Record<string, unknown>;
    this.targetCount = (configData.objectCount as number) || 4;
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // ── Background (soft green, matching Parque palette) ──
    this.add.rectangle(width / 2, height / 2, width, height, 0xa5d6a7);

    // ── Prompt text ──
    const configData = this.input.config.data as Record<string, unknown>;
    const prompt = (configData.prompt as string) || 'Conta as flores!';
    this.promptText = this.add.text(width / 2, 60, prompt, {
      fontSize: '20px',
      fontFamily: 'sans-serif',
      color: '#1b5e20',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // ── Counter display ──
    this.countText = this.add.text(width / 2, height - 60, `0 / ${this.targetCount}`, {
      fontSize: '28px',
      fontFamily: 'sans-serif',
      color: '#2e7d32',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // ── Place flowers (red circles with stems) ──
    this.flowers = [];
    const positions = this.generateFlowerPositions(this.targetCount, width, height);

    for (const pos of positions) {
      // Stem
      this.add.rectangle(pos.x, pos.y + 20, 4, 30, 0x4caf50).setDepth(1);

      // Flower (red circle — placeholder for 🌹)
      const flower = this.add.circle(pos.x, pos.y, 20, 0xe53935);
      flower.setDepth(2);
      flower.setStrokeStyle(2, 0xb71c1c);
      flower.setInteractive({ useHandCursor: true });

      // Tap to count
      flower.on('pointerdown', () => this.tapFlower(flower));
      this.flowers.push(flower);
    }

    // ── Gentle duration indicator (Requirement 12.5) ──
    // A subtle pulsing dot that changes from green → amber → soft red over 3 minutes
    this.createTimerDot(width);
    this.startDurationTimer();
  }

  /** Creates the subtle pulsing timer dot at the top-right */
  private createTimerDot(width: number): void {
    this.timerDot = this.add.circle(width - 30, 30, 8, 0x66bb6a); // Start green
    this.timerDot.setAlpha(0.7);
    this.timerDot.setDepth(10);

    // Gentle pulsing animation
    this.timerDotTween = this.tweens.add({
      targets: this.timerDot,
      scale: { from: 0.8, to: 1.2 },
      alpha: { from: 0.5, to: 0.8 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Color transitions: green → amber at 60s, amber → soft red at 120s
    this.time.delayedCall(60_000, () => {
      if (!this.isCompleted && this.timerDot) {
        this.timerDot.setFillStyle(0xffca28); // Amber
      }
    });
    this.time.delayedCall(120_000, () => {
      if (!this.isCompleted && this.timerDot) {
        this.timerDot.setFillStyle(0xef9a9a); // Soft red (not alarming)
      }
    });
  }

  /** Starts the 3-minute duration timer */
  private startDurationTimer(): void {
    this.timerEvent = this.time.delayedCall(
      CountingMinigame.MAX_DURATION_MS,
      () => this.onTimerExpired()
    );
  }

  /** Called when the 3-minute limit is reached */
  private onTimerExpired(): void {
    if (this.isCompleted) return;

    const { width, height } = this.cameras.main;

    // Stop the timer dot pulse
    if (this.timerDotTween) {
      this.timerDotTween.stop();
    }

    // Show gentle completion signal: visual indicator + message
    const clockEmoji = this.add.text(width / 2, height / 2 - 40, '⏰', {
      fontSize: '48px',
    }).setOrigin(0.5).setAlpha(0).setDepth(20);

    const timeoutMsg = this.add.text(width / 2, height / 2 + 20, 'Tempo esgotado! ✨', {
      fontSize: '22px',
      fontFamily: 'sans-serif',
      color: '#5d4037',
      fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0).setDepth(20);

    // Gentle fade-in animation for the signal
    this.tweens.add({
      targets: [clockEmoji, timeoutMsg],
      alpha: 1,
      duration: 600,
      ease: 'Sine.easeOut',
    });

    // Soft pulsing on the clock emoji
    this.tweens.add({
      targets: clockEmoji,
      scale: { from: 0.9, to: 1.1 },
      duration: 800,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });

    // Auto-complete as success after 2 seconds (don't punish the child)
    this.time.delayedCall(2000, () => {
      this.completeMinigame(true);
    });
  }

  private tapFlower(flower: Phaser.GameObjects.Arc): void {
    // Already counted — ignore
    if (flower.getData('counted')) return;

    flower.setData('counted', true);
    this.currentCount++;

    // Visual feedback: flower grows and gets gold ring
    this.tweens.add({
      targets: flower,
      scale: 1.3,
      duration: 200,
      yoyo: true,
      ease: 'Back.easeOut',
    });
    flower.setStrokeStyle(3, 0xffc107);
    flower.setAlpha(0.7);

    // Update counter
    this.countText.setText(`${this.currentCount} / ${this.targetCount}`);

    // Check completion
    if (this.currentCount >= this.targetCount) {
      this.attempts++;
      this.completeMinigame(true);
    }
  }

  private completeMinigame(success: boolean): void {
    if (this.isCompleted) return; // Prevent double-completion
    this.isCompleted = true;

    // Cancel the duration timer if still running
    if (this.timerEvent) {
      this.timerEvent.remove(false);
    }
    // Stop timer dot animation
    if (this.timerDotTween) {
      this.timerDotTween.stop();
    }
    if (this.timerDot) {
      this.timerDot.setAlpha(0);
    }

    const duration = (Date.now() - this.startTime) / 1000;

    // Brief celebration delay before returning
    this.time.delayedCall(800, () => {
      const output: MinigameOutput = {
        mission_id: this.input.mission_id,
        success,
        score: success ? 1 : 0,
        attempts: Math.max(1, this.attempts),
        duration_seconds: duration,
      };
      this.onComplete(output);
    });

    // Show brief success feedback
    if (success) {
      const { width, height } = this.cameras.main;
      const successText = this.add.text(width / 2, height / 2, '⭐', {
        fontSize: '64px',
      }).setOrigin(0.5).setAlpha(0);

      this.tweens.add({
        targets: successText,
        alpha: 1,
        scale: { from: 0.5, to: 1.5 },
        duration: 600,
        ease: 'Back.easeOut',
      });
    }
  }

  /** Generate non-overlapping positions for flowers */
  private generateFlowerPositions(
    count: number,
    width: number,
    height: number
  ): { x: number; y: number }[] {
    const positions: { x: number; y: number }[] = [];
    const margin = 80;
    const minDist = 70;

    for (let i = 0; i < count; i++) {
      let placed = false;
      let tries = 0;

      while (!placed && tries < 100) {
        const x = margin + Math.random() * (width - margin * 2);
        const y = 120 + Math.random() * (height - 240);

        const tooClose = positions.some(
          (p) => Math.hypot(p.x - x, p.y - y) < minDist
        );

        if (!tooClose) {
          positions.push({ x, y });
          placed = true;
        }
        tries++;
      }

      // Fallback: grid placement
      if (!placed) {
        const cols = Math.ceil(Math.sqrt(count));
        const col = i % cols;
        const row = Math.floor(i / cols);
        positions.push({
          x: margin + col * ((width - margin * 2) / cols),
          y: 140 + row * ((height - 280) / Math.ceil(count / cols)),
        });
      }
    }
    return positions;
  }
}
